import { Formik, Form, Field } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { loginApi } from '../../apis/auth';
import { ChevronIcon } from '../../assets/icons/ChevronIcon';
import { CustomButton } from '../button/Button';
import { FormTextField } from '../input/FormTextField';
import { PhoneInput } from '../input/PhoneInput';
import { setUser } from '../../features/currentUser/currentUserSlice';

import { AnimatePresence, motion } from 'motion/react';

import './LoginForm.scss';

type LoginMethod = 'phone' | 'email';

interface LoginFormValues {
  login: string;
  password: string;
  save: boolean;
  loginMethod: LoginMethod;
}

interface LoginFormProps {
  onSuccess?: () => void;
  showLinks?: boolean;
  showHeader?: boolean;
  className?: string;
}

export const LoginForm = ({
  onSuccess,
  showLinks = true,
  showHeader = false,
  className = '',
}: LoginFormProps) => {
  const prefixValidation = 'validation-';
  const prefix = 'signIn-';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loginError, setLoginError] = useState(false);

  const loginSchema = Yup.object().shape({
    loginMethod: Yup.mixed<LoginMethod>().oneOf(['phone', 'email']).required(),
    login: Yup.string().when('loginMethod', {
      is: 'email',
      then: (schema) =>
        schema
          .email(t(prefixValidation + 'emailInvalid'))
          .required(t(prefixValidation + 'emailRequired')),
      otherwise: (schema) => schema.required(t(prefixValidation + 'phoneRequired')),
    }),
    password: Yup.string()
      .required(t(prefixValidation + 'passwordRequred'))
      .max(50),
  });

  const initialValues: LoginFormValues = {
    login: '',
    password: '',
    save: true,
    loginMethod: 'phone',
  };

  const onSubmit = async ({ save, login, password }: LoginFormValues) => {
    try {
      setLoginError(false);
      const user = await loginApi({ login, password });

      if (!user.accessToken || !user.refreshToken) {
        console.error('Login succeeded but tokens are missing from response', user);
        setLoginError(true);
        return;
      }

      dispatch(
        setUser({
          accessToken: user.accessToken,
          refreshToken: user.refreshToken,
          save,
        })
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setLoginError(true);
      console.error('Login failed', e instanceof Error ? e.message : e);
    }
  };

  return (
    <div className={`login-form ${className}`}>
      {showHeader && (
        <div className="login-form__header">
          <span>{t(prefix + 'header')}</span>
        </div>
      )}

      <Formik
        validationSchema={loginSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {({ values, setFieldValue, setFieldError }) => (
          <Form className="login-form__form">
            {/* Toggle between Phone / Email */}
            <div className="login-form__method-toggle">
              <button
                type="button"
                className="login-form__method-btn"
                onClick={() => {
                  const next: LoginMethod = values.loginMethod === 'phone' ? 'email' : 'phone';

                  setLoginError(false);
                  setFieldValue('loginMethod', next);
                  setFieldValue('login', '');
                  setFieldError('login', undefined);
                }}
              >
                {values.loginMethod === 'phone'
                  ? t(prefix + 'changeToEmail')
                  : t(prefix + 'changeToPhone')}
              </button>
            </div>

            <AnimatePresence mode="wait">
              {values.loginMethod === 'phone' && (
                <motion.div
                  key="phone"
                  initial={{ opacity: 0, translateY: -10 }}
                  animate={{ opacity: 1, translateY: 0 }}
                  exit={{ opacity: 0, translateY: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  <Field
                    name="login"
                    component={PhoneInput}
                    label={t(prefix + 'phonePlaceholder')}
                  />
                </motion.div>
              )}

              {values.loginMethod === 'email' && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, translateY: -10 }}
                  animate={{ opacity: 1, translateY: 0 }}
                  exit={{ opacity: 0, translateY: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  <FormTextField
                    className="!py-0"
                    name="login"
                    type="text"
                    label={t(prefix + 'emailPlaceholder')}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="login-form__password-block">
              <FormTextField
                name="password"
                type="password"
                label={t(prefix + 'passwordPlaceholder')}
              />
              {loginError && (
                <div className="field-error text-danger fs-7 login-form__mobile-error">
                  {t(prefix + 'loginError')}
                </div>
              )}
              <div className="login-form__submit">
                <CustomButton type="submit" btnText={t(prefix + 'submit')}>
                  <ChevronIcon isClick={false} width="50px" height="25px" fill="#fff" />
                </CustomButton>
              </div>
            </div>

            {loginError && (
              <div className="field-error text-danger fs-7 login-form__desktop-error">
                {t(prefix + 'loginError')}
              </div>
            )}
          </Form>
        )}
      </Formik>

      {showLinks && (
        <div className="login-form__links">
          <div className="login-form__link">
            <Link to="/reset-password">{t(prefix + 'resetPassword')}</Link>
          </div>
          {/*  <div className="login-form__link login-form__link_padding">
              <Link to="/sign-up"><a href='replace'>{t(prefix + 'createAccount')}</a></Link>
            </div> */}
        </div>
      )}
    </div>
  );
};
